import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient, TICKET_CATEGORY_LABELS, TICKET_CATEGORY_PICKS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import AttachmentPicker from "@/components/AttachmentPicker";
import { ArrowLeft, PaperPlaneTilt, CheckCircle, Copy, UserCircle, ChatText, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = TICKET_CATEGORY_PICKS;

const inputCls = "h-14 rounded-xl bg-white border-neutral-200 px-4 text-base focus-visible:ring-2 focus-visible:ring-[#24593C]/25 focus-visible:border-[#24593C] focus-visible:ring-offset-0";

export default function PortalNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    client_first_name: "",
    client_last_name: "",
    client_email: "",
    client_phone: "",
    transaction_number: "",
    category: "",
    subject: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Merci d'accepter la politique de confidentialité");
      return;
    }
    const required = ["client_first_name", "client_last_name", "client_email", "category", "subject", "description"];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error("Champs obligatoires manquants");
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("consent_given", "true");
      files.forEach((f) => fd.append("files", f));
      const { data } = await apiClient.post("/portal/tickets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const trackingUrl = `${window.location.origin}/portail/suivi/${data.tracking_token}`;
      setSuccess({ ...data, trackingUrl });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section className="max-w-xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 portal-pop" data-testid="portal-new-success">
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-black/[0.05] p-6 sm:p-8">
          <div className="w-16 h-16 rounded-2xl bg-[#ECF9F1] flex items-center justify-center mb-5">
            <CheckCircle size={34} weight="fill" className="text-[#218A49]" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#1D211F] mb-2">Ticket enregistré&nbsp;!</h1>
          <p className="text-[15px] text-[#68706A] mb-6">Votre demande a été transmise à notre équipe. Un e-mail de confirmation vient de vous être envoyé.</p>

          <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100 overflow-hidden">
            <div className="px-4 py-3.5">
              <div className="text-xs uppercase tracking-wider text-[#68706A] font-medium">Référence</div>
              <div className="font-mono font-semibold text-xl text-[#1D211F] mt-1" data-testid="success-reference">{success.reference}</div>
            </div>
            <div className="px-4 py-3.5">
              <div className="text-xs uppercase tracking-wider text-[#68706A] font-medium">Lien de suivi sécurisé</div>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 text-xs bg-[#F7F6F2] border border-neutral-200 rounded-lg px-3 py-2.5 break-all" data-testid="success-tracking-url">{success.trackingUrl}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(success.trackingUrl); toast.success("Lien copié"); }}
                  className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg border border-neutral-200 hover:bg-neutral-50 active:scale-95 transition-all"
                  data-testid="copy-tracking-url"
                  title="Copier le lien"
                >
                  <Copy size={16} weight="bold" />
                </button>
              </div>
              <p className="text-xs text-[#68706A] mt-2">Conservez ce lien pour consulter ou répondre à votre ticket sans créer de compte.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button onClick={() => navigate(`/portail/suivi/${success.tracking_token}`)} className="bg-[#24593C] hover:bg-[#1B432D] text-white rounded-full h-14 px-6 text-base font-medium active:scale-[0.98] transition-all flex-1" data-testid="goto-ticket-btn">
              Consulter mon ticket
            </Button>
            <Button variant="outline" onClick={() => navigate("/portail")} className="rounded-full h-14 px-6 text-base font-medium border-neutral-200 hover:bg-neutral-50">
              Accueil
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 portal-fade-up" data-testid="portal-new-page">
      <Link to="/portail" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#68706A] hover:text-[#24593C] transition-colors mb-5">
        <ArrowLeft size={16} weight="bold" /> Retour aux options
      </Link>

      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D211F] mb-2">Signaler un litige</h1>
      <p className="text-base text-[#68706A] mb-7">Renseignez les informations ci-dessous. Notre équipe vous répondra rapidement.</p>

      <form onSubmit={submit} className="space-y-4" data-testid="portal-new-form">
        <Section icon={UserCircle} title="Vos coordonnées" step="1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Prénom *"><Input value={form.client_first_name} onChange={(e) => setF("client_first_name", e.target.value)} className={inputCls} required data-testid="input-first_name" /></Field>
            <Field label="Nom *"><Input value={form.client_last_name} onChange={(e) => setF("client_last_name", e.target.value)} className={inputCls} required data-testid="input-last_name" /></Field>
            <Field label="E-mail *"><Input type="email" value={form.client_email} onChange={(e) => setF("client_email", e.target.value)} className={inputCls} required data-testid="input-email" /></Field>
            <Field label="Téléphone"><Input value={form.client_phone} onChange={(e) => setF("client_phone", e.target.value)} className={inputCls} data-testid="input-phone" /></Field>
          </div>
        </Section>

        <Section icon={ChatText} title="Votre demande" step="2">
          <div className="space-y-4">
            <Field label="Numéro de transaction (si applicable)"><Input value={form.transaction_number} onChange={(e) => setF("transaction_number", e.target.value)} className={`${inputCls} font-mono`} placeholder="Ex: TRX-2026-0001" data-testid="input-transaction" /></Field>
            <Field label="Catégorie *">
              <Select value={form.category} onValueChange={(v) => setF("category", v)}>
                <SelectTrigger className={`${inputCls} data-[placeholder]:text-neutral-400`} data-testid="input-category"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{TICKET_CATEGORY_LABELS[c]}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sujet *"><Input value={form.subject} onChange={(e) => setF("subject", e.target.value)} className={inputCls} placeholder="Ex: Produit reçu endommagé" required data-testid="input-subject" /></Field>
            <Field label="Description détaillée *">
              <Textarea value={form.description} onChange={(e) => setF("description", e.target.value)} className="rounded-xl bg-white border-neutral-200 p-4 text-base min-h-[130px] focus-visible:ring-2 focus-visible:ring-[#24593C]/25 focus-visible:border-[#24593C] focus-visible:ring-offset-0 resize-none" placeholder="Décrivez votre situation en détail..." required data-testid="input-description" />
            </Field>
          </div>
        </Section>

        <Section icon={Paperclip} title="Pièces jointes" step="3" optional>
          <p className="text-sm text-[#68706A] mb-3">Ajoutez des photos ou des PDF pour illustrer votre demande.</p>
          <AttachmentPicker files={files} setFiles={setFiles} compact testIdPrefix="portal" />
        </Section>

        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6 space-y-5">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="portal-consent-label">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 data-[state=checked]:bg-[#24593C] data-[state=checked]:border-[#24593C]" data-testid="portal-consent" />
            <span className="text-sm text-[#68706A] leading-relaxed">
              J'accepte que mes données personnelles soient traitées par Ecocasse pour la gestion de ma demande, conformément à la politique de confidentialité (RGPD).
            </span>
          </label>
          <div className="flex flex-col sm:flex-row-reverse gap-3">
            <Button type="submit" disabled={submitting || !consent} className="bg-[#24593C] hover:bg-[#1B432D] text-white rounded-full h-14 px-6 text-base font-medium gap-2 active:scale-[0.98] transition-all flex-1 disabled:opacity-50" data-testid="portal-submit">
              <PaperPlaneTilt size={18} weight="bold" />
              {submitting ? "Envoi en cours..." : "Envoyer la demande"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/portail")} className="rounded-full h-14 px-6 text-base font-medium border-neutral-200 hover:bg-neutral-50">Annuler</Button>
          </div>
        </div>
      </form>
    </section>
  );
}

function Section({ icon: Icon, title, step, optional, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#E7F1EF] text-[#24593C] flex items-center justify-center shrink-0">
          <Icon size={20} weight="duotone" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#68706A] font-medium">Étape {step}{optional ? " · facultatif" : ""}</div>
          <h2 className="font-display text-lg font-semibold tracking-tight text-[#1D211F]">{title}</h2>
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-[#1D211F]">{label}</Label>
      {children}
    </div>
  );
}
